import Divider from '@mui/material/Divider';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React, { type MouseEvent } from 'react';
import styles from 'src/components/Tabs/MarkersTab/MarkersList/MarkersListStyles';
import MarkersActionsButton from './MarkersActionsButton';

interface MarkerNode {
  id: string;
  markIn?: string;
  markOut?: string;
  title?: string;
  description?: string;
}

interface MarkerEdge {
  node: MarkerNode;
  markIn?: string;
  markOut?: string;
}

interface MarkersListProps {
  markers: MarkerEdge[];
  onClickItem: (item: MarkerNode, event: MouseEvent<HTMLTableRowElement>) => void;
  df: boolean;
  editMarkerClickHandler: (item: MarkerNode & { markIn?: string; markOut?: string }) => void;
  deleteMarkerClickHandler: (item: MarkerNode) => void;
  downloadMarkerClickHandler: (item: MarkerNode) => void;
  selectedItem?: string;
  isAwsMedia: boolean;
}

function formatDropframeMarkerValue(str: string | undefined): string {
  if (!str || typeof str !== 'string') return str || '';
  if (/\d{2}:\d{2}:\d{2};\d+/.test(str)) return str;
  const lastIndex = str.lastIndexOf(':');
  if (lastIndex === -1) return str;
  return `${str.substring(0, lastIndex)};${str.substring(lastIndex + 1)}`;
}

const MarkersList: React.FC<MarkersListProps> = ({
  markers,
  onClickItem,
  df,
  editMarkerClickHandler,
  deleteMarkerClickHandler,
  downloadMarkerClickHandler,
  selectedItem,
  isAwsMedia,
}) => {
  const classes = styles();

  const onClickEditMarker = (item: MarkerEdge): void => {
    editMarkerClickHandler({
      ...item,
      ...item.node,
      markIn: df ? formatDropframeMarkerValue(item.markIn) : item.markIn,
      markOut: df && item.markOut ? formatDropframeMarkerValue(item.markOut) : item.markOut,
    });
  };

  const onClickMarkerDownload = (item: MarkerNode): void => {
    downloadMarkerClickHandler(item);
  };

  const onClickDeleteMarker = (item: MarkerNode): void => {
    deleteMarkerClickHandler(item);
  };

  return (
    <>
      <TableHead className={classes.tableHead}>
        <TableRow>
          <TableCell className={classes.timeIn}>Time In</TableCell>
          <TableCell className={classes.timeOut}>Time Out</TableCell>
          <TableCell className={classes.timeName}>Name</TableCell>
          <TableCell className={classes.timeNotes}>Notes</TableCell>
        </TableRow>
      </TableHead>
      <Divider className={classes.divider} />
      {markers.map((item) => (
        <React.Fragment key={item.node.id}>
          <div className={classes.iconAndList}>
            <MarkersActionsButton
              handleEdit={() => onClickEditMarker(item)}
              handleDelete={() => onClickDeleteMarker(item.node)}
              handleDownload={() => onClickMarkerDownload(item.node)}
              markerItem={item.node}
              isAwsMedia={isAwsMedia}
            />
            <TableBody
              className={`${classes.markerItems} ${selectedItem === item.node.id ? classes.selectedMarker : ''}`}
            >
              <TableRow
                id={`cyp-marker-row-${item.node.id}`}
                className={classes.tableRow}
                onClick={(e) => onClickItem(item.node, e)}
              >
                <TableCell className={classes.markerIn}>
                  {df ? formatDropframeMarkerValue(item.node.markIn) : item.node.markIn || ''}
                </TableCell>
                <TableCell className={classes.markerOut}>
                  {df && item.node.markOut ? formatDropframeMarkerValue(item.node.markOut) : item.node.markOut}{' '}
                </TableCell>
                <TableCell className={classes.markerName}>{item.node.title}</TableCell>
                <TableCell className={classes.markerNotes}>{item.node.description}</TableCell>
              </TableRow>
            </TableBody>
          </div>
          <Divider className={classes.divider} />
        </React.Fragment>
      ))}
    </>
  );
};

export default MarkersList;
